import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Radio } from "lucide-react";
import { api } from "@/lib/api";

const BADGES = {
  throne_stolen: {
    label: "THRONE STOLEN",
    icon: "🚨",
    className: "border-yellow-500/50 bg-gradient-to-r from-yellow-500/15 to-red-500/15 text-yellow-200",
  },
  podium_breach: {
    label: "PODIUM BREACH",
    icon: "⚔️",
    className: "border-slate-300/40 bg-gradient-to-r from-slate-300/15 to-amber-600/15 text-slate-100",
  },
  new_challenger: {
    label: "NEW CHALLENGER",
    icon: "⚔️",
    className: "border-blue-500/50 bg-gradient-to-r from-blue-500/15 to-emerald-500/15 text-blue-100",
  },
  new_arena: {
    label: "NEW ARENA",
    icon: "🆕",
    className: "border-violet-500/50 bg-violet-500/15 text-violet-100",
  },
};

function boardHref(ev) {
  const isMY = (ev.region || "").toLowerCase() === "my";
  return `${isMY ? "/my/k/" : "/k/"}${ev.keyword_slug}`;
}

function TickerCard({ ev }) {
  const badge = BADGES[ev.event_type] || BADGES.new_challenger;
  return (
    <Link
      to={boardHref(ev)}
      data-testid={`ticker-item-${ev.event_id}`}
      className="group shrink-0 flex items-center gap-3 glass rounded-full pl-2 pr-5 py-2 hover:border-white/25 transition-colors"
    >
      <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide rounded-full px-2.5 py-1 border ${badge.className}`}>
        <span aria-hidden>{badge.icon}</span> {badge.label}
      </span>
      <span className="text-sm text-white/70 whitespace-nowrap group-hover:text-white transition-colors">
        {ev.message}
      </span>
    </Link>
  );
}

export function LiveTicker({ region = "global" }) {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/commentary", { params: { region } });
        if (alive) setEvents(data.events || []);
      } catch {
        /* silent — ticker is non-critical */
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [region]);

  if (events.length === 0) return null;

  // Duplicate the list so the CSS translateX(-50%) loop is seamless.
  const loop = [...events, ...events];

  return (
    <section className="max-w-6xl mx-auto px-4 mb-6" data-testid="live-ticker">
      <div className="flex items-center gap-2 mb-3 px-1">
        <Radio className="w-4 h-4 text-red-400 animate-pulse" />
        <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">Live Arena Feed</h2>
      </div>
      <div className="ticker-viewport relative overflow-hidden glass rounded-2xl py-2">
        <div
          className="pointer-events-none absolute inset-y-0 left-0 w-16 z-10"
          style={{ background: "linear-gradient(to right, rgba(10,10,16,0.9), transparent)" }}
        />
        <div
          className="pointer-events-none absolute inset-y-0 right-0 w-16 z-10"
          style={{ background: "linear-gradient(to left, rgba(10,10,16,0.9), transparent)" }}
        />
        <div className="ticker-track px-3">
          {loop.map((ev, i) => (
            <TickerCard key={`${ev.event_id}-${i}`} ev={ev} />
          ))}
        </div>
      </div>
    </section>
  );
}
